import Basemap from '@arcgis/core/Basemap';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import {
  getEsriApiKey,
  getEsriMapIdDark,
  getEsriMapIdLight,
} from '@rsos/utils/metaTags';

type AlternativeBasemapMapId = {
  dark?: string;
  light?: string;
};

/**
 * ArcGIS Location Platform custom Basemap Style item IDs, served by the Basemap Styles service.
 * Auth: supports the global esri API key.
 *
 * @see https://developers.arcgis.com/documentation/mapping-and-location-services/mapping/basemaps/custom-basemap-styles/
 */
/**
 * Resolve a basemap key to a {@link Basemap} (custom Location Platform navigation style)
 * or pass the key through unchanged for built-in @arcgis/core basemaps (e.g. imagery).
 *
 * Custom style IDs are only used when `hasAlternativeBasemap` is true:
 * - first from `options.alternativeBasemapItemIds`
 * - then from ESRI map-id meta tags
 * - otherwise fall back to the built-in Esri basemap key
 */
export function esriBasemapFromUiString(
  basemap: __esri.MapProperties['basemap'],
  options: {
    hasAlternativeBasemap?: boolean;
    alternativeBasemapItemIds?: AlternativeBasemapMapId;
  } = {},
): __esri.MapProperties['basemap'] {
  if (basemap && typeof basemap !== 'string') {
    return basemap;
  }
  const key = basemap || 'arcgis-navigation';
  if (!options.hasAlternativeBasemap) {
    return key;
  }
  const resolvedLightItemId =
    options.alternativeBasemapItemIds?.light ||
    getEsriMapIdLight() ||
    undefined;
  const resolvedDarkItemId =
    options.alternativeBasemapItemIds?.dark || getEsriMapIdDark() || undefined;
  // Precedence (per theme): explicit options -> meta tags -> default Esri basemap key.
  const customItemId =
    key === 'arcgis-navigation'
      ? resolvedLightItemId
      : key === 'arcgis-navigation-night'
        ? resolvedDarkItemId
        : undefined;
  if (!customItemId) {
    return key;
  }
  const token = getEsriApiKey();
  const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
  return new Basemap({
    baseLayers: [
      new VectorTileLayer({
        url: `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/items/${customItemId}${tokenQuery}`,
      }),
    ],
  });
}

async function doesBasemapLoad(
  basemap: __esri.MapProperties['basemap'],
): Promise<boolean> {
  if (!basemap || typeof basemap === 'string') {
    return true;
  }

  const basemapInstance =
    basemap instanceof Basemap
      ? basemap
      : new Basemap(basemap as __esri.BasemapProperties);

  try {
    await basemapInstance.load();
    const baseLayers = basemapInstance.baseLayers?.toArray() ?? [];

    const baseLayerLoads = await Promise.allSettled(
      baseLayers.map((layer: __esri.Layer) => layer.load()),
    );

    return baseLayerLoads.every(
      (result: PromiseSettledResult<__esri.Layer>) =>
        result.status === 'fulfilled',
    );
  } catch {
    return false;
  }
}

/**
 * Resolve a basemap with fallback behavior for custom map-id styles:
 * - use custom map-id when it loads
 * - fall back to built-in Esri basemap key when map-id auth/load fails
 */
export async function esriBasemapFromUiStringWithFallback(
  basemap: __esri.MapProperties['basemap'],
  options: {
    hasAlternativeBasemap?: boolean;
    alternativeBasemapItemIds?: AlternativeBasemapMapId;
  } = {},
): Promise<__esri.MapProperties['basemap']> {
  const candidate = esriBasemapFromUiString(basemap, options);

  // Fallback is only relevant for string-key basemaps attempting custom map-id resolution.
  if (!options.hasAlternativeBasemap || typeof basemap !== 'string') {
    return candidate;
  }

  if (await doesBasemapLoad(candidate)) {
    return candidate;
  }

  // Retry with built-in Esri basemap key when custom map-id is unavailable.
  return esriBasemapFromUiString(basemap, {
    ...options,
    hasAlternativeBasemap: false,
  });
}
